import React from 'react';

/**
 * MicrosoftSignInButton — the polished SSO button. Presentational only: it
 * renders the official Microsoft mark, a hover shine, and a spinner for the
 * loading / session-checking states. All authentication behaviour stays in the
 * parent — this component just forwards the click.
 */
export interface MicrosoftSignInButtonProps {
  onClick: () => void;
  /** A sign-in request is in flight. */
  loading?: boolean;
  /** The app is still restoring an existing session. */
  checkingSession?: boolean;
  disabled?: boolean;
}

export const MicrosoftSignInButton: React.FC<MicrosoftSignInButtonProps> = ({
  onClick,
  loading,
  checkingSession,
  disabled,
}) => {
  const busy = Boolean(loading || checkingSession);
  const label = loading ? 'Signing in…' : checkingSession ? 'Checking session…' : 'Sign in with Microsoft';

  /*
   * Swallow clicks while a sign-in is already running or the button is disabled.
   * The button is also `disabled` in those states, but this guards the double-tap
   * window on touch and any programmatic click — without touching the SSO flow,
   * which is still exactly the parent's `onClick`.
   */
  const handleClick = () => {
    if (busy || disabled) return;
    onClick();
  };

  return (
    <button type="button" className="ms-btn" onClick={handleClick} disabled={disabled} aria-busy={busy}>
      {busy ? (
        <span className="ms-btn__spinner" aria-hidden="true" />
      ) : (
        <svg className="ms-btn__logo" viewBox="0 0 21 21" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="1" width="9" height="9" fill="#f25022" />
          <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
          <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
          <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
        </svg>
      )}
      <span className="ms-btn__label">{label}</span>
      <span className="ms-btn__shine" aria-hidden="true" />
    </button>
  );
};

export default MicrosoftSignInButton;
